import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { Box, Stack } from '@mui/material';

import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { Alert } from '@/components/shared/Alert';
import { Snackbar } from '@/components/shared/Snackbar';
import * as state from '@/components/Analysis/state';
import { useSelectedAssemblyTemplateId } from '@/components/Analysis/hooks/useSelectedAssemblyTemplateId';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import {
  selectedStructureId as structureIdState,
  selectedStructureDeploymentLevel as deploymentLevelState,
} from '@/state';

import {
  useAllPointOfInterestTemplateQuery,
  PointOfInterest,
  PointOfInterestTemplateField,
  TemplateFieldTypeEnum,
  useUpdateReportMutation,
  UnitsEnum,
  UnitSystemEnum,
  useDefectsAreaForAssemblyPaintRegionsQuery,
  PoiStatus,
  StructureDeploymentLevel,
  useStructureDeploymentLevelQuery,
  StructureDeploymentLevelQuery,
  useGetPoiStatusLabelsQuery,
  useGetStructureDataQuery,
} from '@/__generated__/graphql';

import { ReportComments } from '../../shared/ReportComments';
import { PaintBlockReportTable } from '../PaintBlockReport/PaintBlockReportTable';
import { usePaintBlockData } from '../PaintBlockReport/usePaintBlockData';

import { NotificationAction, NotificationText, notificationAlertStyles } from '../styles';
import { ReportImagesAccordian } from '../ReportImagesAccordian';
import { TableSkeleton } from '@/components/Analysis/Insights/InsightsExplorer/Decks/TableSkeleton';
import { ReportHeader } from '../ReportHeader';
import { PaintBlockReportRemarksField } from '../PaintBlockReport/PaintBlockReportRemarksField';
import { RemoveChaseItemsDialog } from '../PaintBlockReport/RemoveChaseItemsDialog';
import { Accordion } from '@/components/shared/Accordion';
import { RowConfig } from './types';
import { localiseMeasurement } from '@/utils/unitSystem';

enum StructureCategory {
  PRESSURE = 'PRESSURE',
  COATING_MANAGER = 'COATINGS MANAGER',
}

export const PaintBlockReportContainer = () => {
  const [selectedReport, setSelectedReport] = useAtom(state.selectedReport);
  const [editSelectedReport, setEditSelectedReport] = useState(selectedReport);
  const [snackbarMessage, setSnackbarMessage] = useAtom(state.snackbarMessage);
  const [selectedChaseItems, setSelectedChaseItems] = useState<string[]>([]);
  const [editModeOf, setEditModeOf] = useState('');
  const [removeChaseItemsDialog, setRemoveChaseItemsDialog] = useState(false);
  const structureId = useAtomValue(structureIdState);
  const unitSystem = useAtomValue(state.unitSystem);
  const selectedAssemblyTemplateId = useSelectedAssemblyTemplateId();
  const [deploymentLevel, setDeploymentLevel] = useAtom(deploymentLevelState);
  const setPoiStatusLabels = useSetAtom(state.poiStatusLabels);

  const [showVerifiedAlert, setShowVerifiedAlert] = useState<boolean>(false);
  const [chaseItemHeaders, setChaseItemHeaders] = useState<PointOfInterestTemplateField[]>([]);
  const paintDamagePois = selectedReport?.pointOfInterests || [];

  const [updateReport, { loading: updateReportLoading }] = useUpdateReportMutation();

  // Load POI status labels from database
  const { data: poiStatusLabelsData } = useGetPoiStatusLabelsQuery({
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });

  const { data: poiTemplates } = useAllPointOfInterestTemplateQuery({
    variables: {
      structureId,
      assemblyTemplateId: selectedAssemblyTemplateId || '',
    },
    skip: !structureId,
  });

  // Fetch deployment level if not already set
  useStructureDeploymentLevelQuery({
    variables: {
      structureId,
    },
    skip: !structureId || !!deploymentLevel,
    onCompleted: (data: StructureDeploymentLevelQuery) => {
      if (data?.structure?.deploymentLevel) {
        setDeploymentLevel(data.structure.deploymentLevel);
      }
    },
  });

  // Fetch structure data including category

  const structureIdFromUrl = useGetStructureId();
  const { data: structureData, loading: structureDataLoading } = useGetStructureDataQuery({
    variables: {
      structureId: structureIdFromUrl ?? '',
    },
    skip: !structureIdFromUrl,
  });
  const isPressureStructure = useMemo(() => {
    const category = structureData?.structure?.category?.toUpperCase();
    return category === StructureCategory.PRESSURE;
  }, [structureData?.structure?.category]);

  const skipDefectsAreaQuery =
    selectedReport?.templateFieldData && structureId && selectedReport?.paintRegion?.id;

  const { data: defectsArea } = useDefectsAreaForAssemblyPaintRegionsQuery({
    variables: {
      input: {
        structureIds: [structureId!],
        paintRegionIds: [selectedReport?.paintRegion?.id!],
      },
    },
    skip: skipDefectsAreaQuery,
  });

  useEffect(() => {
    if (selectedReport) {
      setEditSelectedReport(selectedReport);
    }
    setSelectedChaseItems([]);
  }, [selectedReport]);

  // Set POI status labels in atom when data is loaded
  useEffect(() => {
    setPoiStatusLabels(poiStatusLabelsData?.setting ?? undefined);
  }, [poiStatusLabelsData?.setting, setPoiStatusLabels]);

  const paintDamageTemplate = useMemo(() => {
    return (
      poiTemplates?.allPointOfInterestTemplate?.find(
        (template) => template.name === 'Paint Damage'
      ) || undefined
    );
  }, [poiTemplates]);

  const baseColumnConfig = useMemo(
    () => ({
      defaultVisible: true,
      isReadOnly: true,
      isRequired: false,
      options: [],
      serverSide: false,
      type: TemplateFieldTypeEnum.Input,
    }),
    []
  );

  useEffect(() => {
    const staticFields: PointOfInterestTemplateField[] = [
      {
        ...baseColumnConfig,
        key: 'name',
        name: 'Chase Item',
        id: 'chase-item-name',
      },
      {
        ...baseColumnConfig,
        key: 'assembly',
        name: 'Component',
        id: 'item-name',
      },
      {
        ...baseColumnConfig,
        key: 'accessibility',
        name: 'Accessibility requirement',
        id: 'accessibility-requirement',
      },
      {
        ...baseColumnConfig,
        key: 'workpack',
        name: 'WorkPack',
        id: 'workpack',
      },
      {
        ...baseColumnConfig,
        key: 'status',
        name: 'Status',
        id: 'status',
      },
    ];

    setChaseItemHeaders([...staticFields, ...(paintDamageTemplate?.fields || [])]);
  }, [baseColumnConfig, paintDamageTemplate?.fields]);

  const assetType = useMemo(() => {
    const allTypes = [
      { name: 'Structure', key: 'structure' },
      { name: 'Pressure', key: 'pressure' },
      { name: 'Combined', key: 'combined' },
    ];
    return allTypes;
  }, []);

  const unitSymbol = useMemo(() => {
    return unitSystem === UnitSystemEnum.Imperial ? '(ft²)' : '(m²)';
  }, [unitSystem]);

  const formatRowValue = useCallback(
    (value: string, unit: UnitsEnum | undefined) => {
      if (value === 'NaN' || value === undefined) {
        return 'N/A';
      } else if (unit) {
        return localiseMeasurement({
          unitSystem,
          sourceUnit: unit,
          value: Number(value),
          showUnitLabel: false,
        });
      } else {
        return value;
      }
    },
    [unitSystem]
  );

  const actualValuesColumns = useMemo(() => {
    return [
      {
        ...baseColumnConfig,
        name: `Total Area Painted ${unitSymbol}`,
        key: 'actual_total_area_painted',
        unit: UnitsEnum.SquareMeter,
        id: 'actual-total-area-painted',
      },
      {
        ...baseColumnConfig,
        name: 'POB',
        key: 'actual_pob',
        id: 'actual-pob',
        unit: undefined,
      },
      {
        ...baseColumnConfig,
        name: 'Chase items resolved',
        key: 'actual_chase_items_resolved',
        id: 'actual-chase-items-resolved',
        unit: undefined,
      },
      {
        ...baseColumnConfig,
        name: 'Manhours spent (Days)',
        key: 'actual_manhours_spent',
        id: 'actual-manhours-spent',
        unit: undefined,
      },
    ];
  }, [baseColumnConfig, unitSymbol]);

  const actualValuesData = useMemo(() => {
    if (!selectedReport || !selectedReport.templateFieldData) return { rows: [], columns: [] };
    const templateFieldData = selectedReport?.templateFieldData;

    const columns = [
      {
        ...baseColumnConfig,
        key: 'asset_type',
        name: 'Asset type',
        id: 'static-asset-type',
      },
      ...actualValuesColumns.map((col, index) => ({
        ...baseColumnConfig,
        unit: col.unit,
        key: col.key,
        name: col.name,
        id: col.key + index,
      })),
    ];

    const rows = assetType.map((type) => {
      const selectedKeys = actualValuesColumns.reduce((accumulator, col) => {
        const key = `${type.key}_${col.key}`;
        const rawValue = templateFieldData[key];

        const value = formatRowValue(rawValue, col.unit);

        accumulator[col.name] = value;
        return accumulator;
      }, {} as { [key: string]: string });

      return { 'Asset type': type.name, ...selectedKeys } as RowConfig;
    });

    return { rows, columns };
  }, [selectedReport, baseColumnConfig, actualValuesColumns, assetType, formatRowValue]);

  const blockOverViewColumns = useMemo(() => {
    return [
      {
        name: `Total Corroded Area ${unitSymbol}`,
        key: 'total_corroded_area',
        unit: UnitsEnum.SquareMeter,
      },
      {
        name: `Total Area ${unitSymbol}`,
        key: 'total_area',
        unit: UnitsEnum.SquareMeter,
      },
      {
        name: `Ri5 ${unitSymbol}`,
        key: 'ri5',
        unit: UnitsEnum.SquareMeter,
      },
      { name: `Ri4 ${unitSymbol}`, key: 'ri4', unit: UnitsEnum.SquareMeter },
      { name: `Chase Items`, key: 'chase_items' },
      {
        name: `Proposed paint area ${unitSymbol}`,
        key: 'proposed_paint',
        unit: UnitsEnum.SquareMeter,
      },
      { name: `Manhours (Days)`, key: 'man_hours' },
    ];
  }, [unitSymbol]);

  const calculateCorrodedArea = (areas: { [key: string]: number }) =>
    Object.values(areas).reduce((sum, value) => sum + (value ?? 0), 0);

  const getPaintBlockTemplateData = useCallback(() => {
    const totalArea =
      defectsArea?.defectsAreaForAssemblyPaintRegions.defectsByPaintBlock[0]?.totalArea;

    const riValues = new Set(['Ri5', 'Ri4', 'Ri3', 'Ri2', 'Ri1', 'Ri0']);
    const coatingConditionValues = new Set(['Light', 'Moderate', 'Heavy']);
    const substrateConditionValues = new Set([
      'Scab/Scaling',
      'Coating Breakdown',
      'Corrosion Nodule',
    ]);

    const extractedValues = totalArea?.reduce((accumulator: { [key: string]: number }, area) => {
      if (area && riValues.has(area.type)) {
        accumulator[area.type.toLowerCase()] = area.value;
      }
      return accumulator;
    }, {});

    const { ri5, ri4, ri3, ri2, ri1, ri0 } = extractedValues || {};

    const structureCorrodedArea =
      totalArea?.reduce((accumulator: { [key: string]: number }, area) => {
        if (area && coatingConditionValues.has(area.type)) {
          accumulator[area.type.toLowerCase()] = area.value;
        }
        return accumulator;
      }, {}) ?? {};

    const substrateCorrodedArea =
      totalArea?.reduce((accumulator: { [key: string]: number }, area) => {
        if (area && substrateConditionValues.has(area.type)) {
          accumulator[area.type.toLowerCase()] = area.value;
        }
        return accumulator;
      }, {}) ?? {};

    const total_corroded_area =
      calculateCorrodedArea(structureCorrodedArea) || calculateCorrodedArea(substrateCorrodedArea);

    const chase_items = selectedReport?.pointOfInterests?.length;

    let total_area = ri5 + ri4 + ri3 + ri2 + ri1 + ri0;

    if (isNaN(total_area)) {
      total_area = 0;
    }

    const manHoursKey = paintDamageTemplate?.fields.find((field) => field.key === 'man_hours')?.key;
    const proposedPaintAreaKey = paintDamageTemplate?.fields.find(
      (field) => field.key === 'proposed_paint_area'
    )?.key;

    const { man_hours, proposed_paint } = selectedReport?.pointOfInterests?.reduce(
      (accumulator, poi) => {
        let poiArea: number = 0;
        let poiManHours: number = 0;

        if (proposedPaintAreaKey)
          poiArea = Number(poi.templateFieldData?.[proposedPaintAreaKey] || 0);
        if (manHoursKey) poiManHours = Number(poi.templateFieldData?.[manHoursKey] || 0);

        const validPoiArea = Number.isNaN(poiArea) ? 0 : poiArea;
        const validPoiManHours = Number.isNaN(poiManHours) ? 0 : poiManHours;

        return {
          proposed_paint:
            (Number.isNaN(accumulator.proposed_paint) ? 0 : accumulator.proposed_paint) +
            validPoiArea,
          man_hours:
            (Number.isNaN(accumulator.man_hours) ? 0 : accumulator.man_hours) + validPoiManHours,
        };
      },
      { proposed_paint: 0, man_hours: 0 } // Initial accumulator values
    ) || { man_hours: 0, proposed_paint: 0 };

    return {
      ri5,
      ri4,
      ri3,
      ri2,
      ri1,
      ri0,
      total_corroded_area,
      total_area,
      chase_items,
      man_hours,
      proposed_paint,
    };
  }, [
    defectsArea?.defectsAreaForAssemblyPaintRegions.defectsByPaintBlock,
    paintDamageTemplate?.fields,
    selectedReport?.pointOfInterests,
  ]);

  const currentReportPois = useMemo(() => {
    return selectedReport?.pointOfInterests;
  }, [selectedReport?.pointOfInterests]);

  const validReportPois = useMemo(() => {
    return currentReportPois?.filter((poi) => poi.status !== PoiStatus.Completed);
  }, [currentReportPois]);

  const invalidReportPois = useMemo(() => {
    return currentReportPois?.filter((poi) => poi.status === PoiStatus.Completed);
  }, [currentReportPois]);

  const updateReportPois = useCallback(
    async (templateFieldData?: { [key: string]: string }) => {
      const { data } = await updateReport({
        variables: {
          input: {
            ...(templateFieldData && { templateFieldData }),
            reportId: selectedReport?.id!,
            pointOfInterestsIds: validReportPois?.map((poi) => poi.id),
            removedPointOfInterestsIds: invalidReportPois?.map((poi) => poi.id),
          },
        },
      });
      if (data?.updateReport) {
        setSelectedReport((previous) => {
          if (!previous) return previous;
          return {
            ...previous,
            templateFieldData: templateFieldData ?? previous.templateFieldData,
            files: data?.updateReport?.files,
            selectedPointOfInterestImages: data?.updateReport?.selectedPointOfInterestImages,
            allPointOfInterestPdfs: data?.updateReport?.allPointOfInterestPdfs,
            pointOfInterests: previous?.pointOfInterests?.filter(
              (poi) => poi.status !== PoiStatus.Completed
            ),
          };
        });
      }
    },
    [invalidReportPois, selectedReport?.id, setSelectedReport, updateReport, validReportPois]
  );

  useEffect(() => {
    if (currentReportPois?.length === validReportPois?.length) return;
    updateReportPois();
  }, [currentReportPois, updateReportPois, validReportPois?.length]);

  const blockOverview = useMemo(() => {
    if (!selectedReport) return { rows: [], columns: [] };
    let templateFieldData = selectedReport?.templateFieldData;
    if (!templateFieldData) {
      const originalData = getPaintBlockTemplateData();
      if (!originalData) return { rows: [], columns: [] };

      const structureData = Object.fromEntries(
        Object.entries(originalData).map(([key, value]) => [`pressure_${key}`, value])
      );
      const combinedData = Object.fromEntries(
        Object.entries(originalData).map(([key, value]) => [`combined_${key}`, value])
      );

      templateFieldData = {
        ...structureData,
        ...combinedData,
        remarks: '',
      };

      updateReportPois(templateFieldData);
    }

    const columns = [
      {
        ...baseColumnConfig,
        key: 'asset_type',
        name: 'Asset type',
        id: 'static-asset-type',
      },
      ...blockOverViewColumns.map((col, index) => ({
        ...baseColumnConfig,
        unit: col.unit,
        key: col.key,
        name: col.name,
        id: col.key + index,
      })),
    ];

    let filteredAssetTypes = assetType;
    // Filter out Structure and Pressure rows if deployment level is FACILITY_LEVEL for BlockOverview
    if (deploymentLevel === StructureDeploymentLevel.FacilityLevel) {
      filteredAssetTypes = assetType.filter((type) => type.key === 'combined');
    }

    // Calculate chase items based on structure category
    const poiCount = selectedReport?.pointOfInterests?.length ?? 0;
    const existingPressureChaseItems = Number(templateFieldData['pressure_chase_items'] || 0);
    const existingStructureChaseItems = Number(templateFieldData['structure_chase_items'] || 0);

    // Override chase items based on isPressureStructure
    const pressureChaseItems = isPressureStructure ? poiCount : existingPressureChaseItems;
    const structureChaseItems = isPressureStructure ? existingStructureChaseItems : poiCount;
    const combinedChaseItems = pressureChaseItems + structureChaseItems;

    // Check if chase items values need to be updated on the backend
    const needsTemplateFieldDataUpdate =
      Number(templateFieldData['pressure_chase_items']) !== pressureChaseItems ||
      Number(templateFieldData['structure_chase_items']) !== structureChaseItems ||
      Number(templateFieldData['combined_chase_items']) !== combinedChaseItems;

    if (needsTemplateFieldDataUpdate && !structureDataLoading) {
      const updatedTemplateFieldData = {
        ...templateFieldData,
        pressure_chase_items: String(pressureChaseItems),
        structure_chase_items: String(structureChaseItems),
        combined_chase_items: String(combinedChaseItems),
      };
      updateReportPois(updatedTemplateFieldData);
    }

    const rows = filteredAssetTypes.map((type) => {
      const selectedKeys = blockOverViewColumns.reduce((accumulator, col) => {
        const key = `${type.key}_${col.key}`;
        let rawValue = templateFieldData[key];

        // Override chase_items values
        if (key === 'pressure_chase_items') {
          rawValue = pressureChaseItems;
        } else if (key === 'structure_chase_items') {
          rawValue = structureChaseItems;
        } else if (key === 'combined_chase_items') {
          rawValue = combinedChaseItems;
        }

        const value = formatRowValue(rawValue, col.unit);
        accumulator[col.name] = value;
        return accumulator;
      }, {} as { [key: string]: string });

      return { 'Asset type': type.name, ...selectedKeys } as RowConfig;
    });

    return { rows, columns };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedReport,
    baseColumnConfig,
    blockOverViewColumns,
    assetType,
    deploymentLevel,
    getPaintBlockTemplateData,
    updateReportPois,
    formatRowValue,
    isPressureStructure,
  ]);

  const chaseItemColumns = useMemo(() => {
    return chaseItemHeaders
      .filter(
        ({ defaultVisible, name }) =>
          defaultVisible &&
          name !== 'Service' &&
          name !== 'Pressure' &&
          name !== 'Date of completion' &&
          name !== 'Recommendation Basis' &&
          name !== 'Acceptance Of Work'
      )
      .map((col) => ({
        ...col,
        name: col.unit === UnitsEnum.SquareMeter ? `${col.name} ${unitSymbol}` : col.name,
      }));
  }, [chaseItemHeaders, unitSymbol]);

  const chaseItemsRows = usePaintBlockData({
    pointOfInterests: paintDamagePois as PointOfInterest[],
    columns: chaseItemColumns,
  });

  const { shouldShow, content } = snackbarMessage;
  const navbarHeight = useNavbarHeight();

  const closeSnack = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarMessage({ shouldShow: false, content: undefined });
  };

  const closeNotification = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowVerifiedAlert(false);
  };

  const handleDismissAlert = () => {
    setShowVerifiedAlert(false);
  };

  const onUpdateReport = useCallback(() => {
    if (!editSelectedReport) return;
    updateReport({
      variables: {
        input: {
          templateFieldData: editSelectedReport.templateFieldData,
          reportId: selectedReport?.id!,
        },
      },
    })
      .then((response) => {
        const newTemplateFieldData = response.data?.updateReport.templateFieldData;
        setSelectedReport((previous) => {
          if (!previous) return previous;
          return {
            ...previous,
            templateFieldData: newTemplateFieldData,
            createdAt: previous.createdAt || '',
          };
        });

        setEditModeOf('');
        setSnackbarMessage({
          shouldShow: true,
          content: <Alert severity="success">Report updated successfully</Alert>,
        });
      })
      .catch(() => {
        setSnackbarMessage({
          shouldShow: true,
          content: <Alert severity="error">Failed to update Report</Alert>,
        });
      });
  }, [editSelectedReport, selectedReport?.id, setSelectedReport, setSnackbarMessage, updateReport]);

  const onCancelEdit = useCallback(() => {
    setEditModeOf('');
    setEditSelectedReport(selectedReport);
  }, [selectedReport]);

  return (
    <>
      <ReportHeader />

      <Box
        sx={{
          height: '80vh',
          overflowY: 'auto',
        }}
      >
        <Stack direction="column">
          {updateReportLoading || structureDataLoading ? (
            <TableSkeleton />
          ) : (
            <>
              <Accordion
                title="Remarks"
                isEditing={editModeOf === 'remarks'}
                onEditClick={() => setEditModeOf(editModeOf === 'remarks' ? '' : 'remarks')}
                onSaveClick={onUpdateReport}
                onCancelClick={onCancelEdit}
                isDisabled={editModeOf !== '' && editModeOf !== 'remarks'}
              >
                <PaintBlockReportRemarksField
                  isEditMode={editModeOf === 'remarks'}
                  editSelectedReport={editSelectedReport}
                  setEditSelectedReport={setEditSelectedReport}
                />
              </Accordion>

              <Accordion
                title="Block Overview"
                isEditing={editModeOf === 'block'}
                onEditClick={() => setEditModeOf(editModeOf === 'block' ? '' : 'block')}
                onSaveClick={onUpdateReport}
                onCancelClick={onCancelEdit}
                isDisabled={editModeOf !== '' && editModeOf !== 'block'}
              >
                <PaintBlockReportTable
                  columns={blockOverview.columns}
                  rows={blockOverview.rows}
                  isEditMode={editModeOf === 'block'}
                  setEditSelectedReport={setEditSelectedReport}
                  tableName="blockOverview"
                  isPressureStructure={isPressureStructure}
                />
              </Accordion>

              <Accordion
                title="Actual Values"
                subTitle="(to be filled upon resolution)"
                isEditing={editModeOf === 'actualValues'}
                onEditClick={() =>
                  setEditModeOf(editModeOf === 'actualValues' ? '' : 'actualValues')
                }
                onSaveClick={onUpdateReport}
                onCancelClick={onCancelEdit}
                isDisabled={editModeOf !== '' && editModeOf !== 'actualValues'}
              >
                <PaintBlockReportTable
                  columns={actualValuesData.columns}
                  rows={actualValuesData.rows}
                  isEditMode={editModeOf === 'actualValues'}
                  setEditSelectedReport={setEditSelectedReport}
                  tableName="actualValues"
                />
              </Accordion>
            </>
          )}
          {chaseItemsRows.length > 0 && (
            <Accordion
              title="Chase Items"
              isEditing={false}
              onRemoveClick={() => setRemoveChaseItemsDialog(true)}
              showRemoveButton={selectedChaseItems.length > 0}
            >
              <PaintBlockReportTable
                columns={chaseItemColumns}
                rows={chaseItemsRows}
                isEditMode={false}
                setSelectedChaseItems={setSelectedChaseItems}
                selectedChaseItems={selectedChaseItems}
                tableName="chaseItems"
                isPressureStructure={isPressureStructure}
              />
            </Accordion>
          )}
          <ReportImagesAccordian />
        </Stack>
      </Box>
      <ReportComments />
      {removeChaseItemsDialog && (
        <RemoveChaseItemsDialog
          open={removeChaseItemsDialog}
          onClose={() => setRemoveChaseItemsDialog(false)}
          selectedChaseItems={selectedChaseItems}
          setSelectedReport={setSelectedReport}
          selectedReport={selectedReport}
          setSnackbarMessage={setSnackbarMessage}
        />
      )}
      <Snackbar open={shouldShow} onClose={closeSnack}>
        {content}
      </Snackbar>
      <Snackbar
        open={showVerifiedAlert}
        onClose={closeNotification}
        sxProps={{ top: `${navbarHeight + 60}px !important` }}
        persistent
      >
        <Alert
          severity="info"
          action={<AbyssIcon iconName="close" pointer onClick={handleDismissAlert} />}
          sxProps={notificationAlertStyles}
        >
          <Stack gap="16px" direction="row" alignItems="center" justifyContent="space-between">
            <NotificationText>
              POI's are now verified, Please refresh to add post inspection data to this report
            </NotificationText>
            // eslint-disable-next-line no-console
            <NotificationAction onClick={() => console.log('refresh')}>Refresh</NotificationAction>
          </Stack>
        </Alert>
      </Snackbar>
    </>
  );
};
